import {
  useEffect, useRef, useState, useMemo,
} from 'react';
import {
  useApiClient,
  useFetch,
} from '@marageti/z4-lib';
import {
  Message, GetParticipantsResponse, MessageType, Conversation, TripMatch, TripRequest,
} from '@marageti/z4-sdk/lib/travel';
import useSWRInfinite from 'swr/infinite';
import { usePusherStore } from '../store';
import debugPromise from '../utils/debug-promise';

const useChatData = ({
  conversationId,
}: {
  conversationId?: string,
}) => {
  const limit = 30;
  const apiClient = useApiClient();
  const conversation = useFetch<Conversation>(apiClient.conversationsClient.getConversation, [conversationId], !!conversationId);
  const participants = useFetch<GetParticipantsResponse>(apiClient.conversationsClient.getParticipants, [conversationId ?? undefined], conversationId !== undefined);

  const getKey = (pageIndex: number, previousPageData: Message[] | null) => {
    if (previousPageData && previousPageData.length === 0) return null; // Stop fetching
    if (!conversationId) return null;

    const params = { limit, offset: previousPageData ? previousPageData[0]?.id : undefined };
    const key = [conversationId, params];
    return key;
  };

  const fetcher = async ([id, params]: [string | null, { limit: number, offset?: string }]) => {
    if (typeof id !== 'string') {
      return [];
    }
    try {
      return await apiClient.conversationsClient.getMessages(id, { params });
    } catch (error) {
      console.error('Fetcher: Error:', error);
      throw error;
    }
  };

  // Fetch messages in parallel with the conversation request. getKey already guards on
  // !conversationId, and the fetcher short-circuits when the id is not a string.
  const paginatedMessages = useSWRInfinite<Message[]>(getKey, fetcher);

  const loadMoreMessages = async () => {
    if (!paginatedMessages.isValidating) {
      debugPromise('useZicassoChat:loadMoreMessages.setSize', paginatedMessages.setSize((size) => size + 1));
    }
  };

  const sortedMessages = useMemo(() => {
    if (!paginatedMessages.data) return [];
    return [...paginatedMessages.data].reverse().flat();
  }, [paginatedMessages.data]);

  const hasMoreMessages = useMemo(() => {
    if (!paginatedMessages.data) return true;
    // Check if validating during initial load phase
    if (paginatedMessages.isValidating && paginatedMessages.data.length === 0) {
      return true;
    }

    const pages = paginatedMessages.data;
    const lastPage = pages[pages.length - 1] ?? [];

    return (lastPage.length > 0);
  }, [paginatedMessages.data, paginatedMessages.isValidating]);

  const messages = {
    data: sortedMessages,
    isLoading: paginatedMessages.isLoading,
    isValidating: paginatedMessages.isValidating,
    error: paginatedMessages.error,
    mutate: paginatedMessages.mutate,
  };

  return {
    conversation,
    participants,
    messages,
    loadMoreMessages,
    hasMoreMessages,
  };
};

type UseZicassoChatProps = {
  conversationId?: string,
  viewerId?: string,
  /**
   * Optional trip match / trip request IDs. When provided, the hook keeps their SWR caches
   * in sync after a message is fully processed by the backend (MESSAGE_PROCESSED Pusher event)
   * so message-state side effects on the trip match and trip request are reflected immediately.
   */
  tripMatchId?: string,
  tripRequestId?: string,
};

const useZicassoChat = ({
  conversationId,
  viewerId,
  tripMatchId,
  tripRequestId,
}: UseZicassoChatProps) => {
  const [lastSeenMessageId, setLastSeenMessageId] = useState<string | undefined>(undefined);
  const initializedLastSeenMessageId = useRef<boolean>(false);

  const apiClient = useApiClient();
  const {
    conversation,
    participants,
    messages,
    loadMoreMessages,
    hasMoreMessages,
  } = useChatData({ conversationId });

  // Fall back to IDs available on the conversation when the caller does not have them
  // (e.g. routes without :tripMatchId/:tripRequestId in the URL).
  const effectiveTripMatchId = tripMatchId ?? conversation.data?.tripMatchId;
  const effectiveTripRequestId = tripRequestId ?? conversation.data?.tripRequestId;

  // Shared SWR cache with the consumer container — calling mutate here refreshes
  // the data wherever the same key is used.
  const tripMatch = useFetch<TripMatch>(apiClient.tripMatchesClient.getTripMatch, [effectiveTripMatchId], !!effectiveTripMatchId);
  const tripRequest = useFetch<TripRequest>(apiClient.tripRequestsClient.getTripRequest, [effectiveTripRequestId], !!effectiveTripRequestId);

  // Only set lastSeenMessageId once when conversation is loaded
  useEffect(() => {
    if (initializedLastSeenMessageId.current || !conversation?.data) return;

    const viewerMessageState = conversation.data.messageState.find((m) => m.personId === viewerId);
    setLastSeenMessageId(viewerMessageState?.lastMessageRead);
    initializedLastSeenMessageId.current = true;
  }, [conversation]);

  const sendMessage = async (message: Partial<Message>) => {
    if (!conversationId || !message.text) {
      throw new Error('Missing conversationId or message text');
    }

    const messageToSend = {
      conversationId,
      messageType: MessageType.Text,
      ...message,
    };

    try {
      const postMessage = await apiClient.conversationsClient.postMessage(conversationId, messageToSend);
      // Update last seen message id
      setLastSeenMessageId(postMessage.id);
    } catch (error: any) {
      console.log(error);
      throw error;
    }
  };

  const updateMessages = async () => {
    await messages.mutate();
  };

  // PUSHER
  const channelName = conversationId ? `presence-${conversationId}` : '';

  const {
    bindToEvent,
    ready,
    subscribeToChannel,
    subscribedChannels,
    unbindFromEvent,
    unsubscribeFromChannel,
  } = usePusherStore();

  // Subscribe to Pusher Channel
  useEffect(() => {
    if (ready && channelName) {
      subscribeToChannel(channelName);
    }

    return () => {
      if (ready) {
        unsubscribeFromChannel(channelName);
      }
    };
  }, [ready, channelName]);

  // Listen to new messages and update messages and conversation
  useEffect(() => {
    if (subscribedChannels[channelName]) {
      bindToEvent(channelName, MessageType.Text, () => {
        debugPromise('useZicassoChat:pusher.TEXT.updateMessages', updateMessages());
      });

      bindToEvent(channelName, 'MESSAGE_READ', () => {
        debugPromise('useZicassoChat:pusher.MESSAGE_READ.conversation.mutate', conversation.mutate());
      });

      bindToEvent(channelName, 'PERSON_ADDED', () => {
        debugPromise('useZicassoChat:pusher.PERSON_ADDED.participants.mutate', participants.mutate());
      });

      // Fired by travel-service after all create-message side effects are applied
      // (trip match + trip request message state updates and notifications).
      bindToEvent(channelName, 'MESSAGE_PROCESSED', () => {
        if (effectiveTripMatchId) {
          debugPromise('useZicassoChat:pusher.MESSAGE_PROCESSED.tripMatch.mutate', tripMatch.mutate());
        }
        if (effectiveTripRequestId) {
          debugPromise('useZicassoChat:pusher.MESSAGE_PROCESSED.tripRequest.mutate', tripRequest.mutate());
        }
      });
    }

    return () => {
      if (subscribedChannels[channelName]) {
        unbindFromEvent(channelName, MessageType.Text);
        unbindFromEvent(channelName, 'MESSAGE_READ');
        unbindFromEvent(channelName, 'PERSON_ADDED');
        unbindFromEvent(channelName, 'MESSAGE_PROCESSED');
      }
    };
  }, [subscribedChannels, effectiveTripMatchId, effectiveTripRequestId]);

  return {
    conversation: conversation.data,
    isError: !!conversation.error || !!participants.error || !!messages.error,
    isLoading: conversation.isLoading || participants.isLoading || messages.isLoading,
    isValidating: messages.isValidating,
    messages: messages.data,
    participants: participants.data,
    lastSeenMessageId,
    updateMessages,
    sendMessage,
    loadMoreMessages,
    hasMoreMessages,
  };
};

export default useZicassoChat;
